/**
 * Fetches every page of a MarkLogic-backed `/api/map/.../features` query,
 * not just the first. Single shared implementation - the endpoint's
 * per-request page size is hard-capped at 5000 by the backend (see
 * focus-client/map-client.js's own clamp), so any query with more results
 * than the requested page size used to be silently truncated after page 1
 * (Tracks, Observations, Agents, and individual track traces all went
 * through this same one-page-only path before).
 *
 * The exact "how many results are there in total" field on this endpoint
 * isn't pinned down in code yet (mirror endpoint utils/heatmapUtil.js uses
 * `metadata.total` for its heatmap variant of the same endpoint family, so
 * that's the primary guess) - probed defensively across a few likely
 * shapes, and if none of them resolve to a sane number, this behaves
 * exactly like the old single-page fetch always did: no infinite loop, no
 * duplicate requests, just the first page, same as before.
 *
 * @param {(page: {start: number, pageLength: number}) => Promise<object>} fetchPage
 *   Fetches one page given `{start, pageLength}` and must resolve to the
 *   raw API response (`{ features: [...], ... }`) or `{ isError: true, error }`.
 * @param {object} [options]
 * @param {number} [options.start=1] - first page's start offset.
 * @param {number} [options.pageLength=5000] - features requested per page.
 * @param {number} [options.maxParallelRequests=3] - concurrent page fetches
 *   once the total is known, so a large result set doesn't fire hundreds of
 *   requests at once.
 * @returns {Promise<object>} the first page's response with `.features`
 *   replaced by the full merged array across every page. If the first page
 *   errors, that error response is returned unchanged so existing
 *   `response.isError` / 401 handling at call sites keeps working untouched.
 */
const fetchAllFeaturePages = async (
  fetchPage,
  { start = 1, pageLength = 5000, maxParallelRequests = 3 } = {}
) => {
  const firstPage = await fetchPage({ start, pageLength });
  if (!firstPage || firstPage.isError) return firstPage;

  const firstFeatures = Array.isArray(firstPage.features)
    ? firstPage.features
    : [];

  const rawTotal =
    (firstPage.metadata && firstPage.metadata.total) ??
    firstPage.total ??
    firstPage['total-results'];
  const total = Number(rawTotal);

  if (!Number.isFinite(total) || total <= start - 1 + firstFeatures.length) {
    return firstPage;
  }

  const remainingStarts = [];
  for (
    let nextStart = start + pageLength;
    nextStart <= total;
    nextStart += pageLength
  ) {
    remainingStarts.push(nextStart);
  }

  const allFeatures = firstFeatures.slice();
  for (
    let index = 0;
    index < remainingStarts.length;
    index += maxParallelRequests
  ) {
    const batchStarts = remainingStarts.slice(
      index,
      index + maxParallelRequests
    );
    // Sequential await across batches is deliberate - each batch waits for
    // the previous one so a huge result set fetches in bounded waves of
    // maxParallelRequests, not all at once.
    // eslint-disable-next-line no-await-in-loop
    const pages = await Promise.all(
      batchStarts.map(pageStart => fetchPage({ start: pageStart, pageLength }))
    );
    pages.forEach(page => {
      if (page && !page.isError && Array.isArray(page.features)) {
        allFeatures.push(...page.features);
      }
    });
  }

  return { ...firstPage, features: allFeatures };
};

export { fetchAllFeaturePages };
